/**
 * Discount Apply Component
 * Handles discount code application and removal in cart drawer
 */

const DISCOUNT_APPLY_MESSAGES = {
  alreadyApplied: 'クーポンコードは既に適用されています',
  invalid: '無効なクーポンコードです',
  unableToApply: 'クーポンコードを適用できませんでした',
  applyFailed: 'クーポンコードの適用に失敗しました',
  refreshFailed: '表示の更新に失敗しました。カートを閉じて再度開き、クーポンの適用状況をご確認ください',
  applySuccess: 'クーポンコードが適用されました'
};

const CART_DRAWER_REPLACE_SELECTORS = ['cart-drawer-items', '.drawer__footer'];

/**
 * Replace cart drawer DOM nodes from rendered section HTML
 */
function replaceCartDrawerFromHtml(htmlString) {
  const html = new DOMParser().parseFromString(htmlString, 'text/html');

  for (const selector of CART_DRAWER_REPLACE_SELECTORS) {
    const targetElement = document.querySelector(selector);
    const sourceElement = html.querySelector(selector);

    if (!targetElement || !sourceElement) {
      throw new Error(`Cart drawer update failed: missing ${selector}`);
    }

    targetElement.replaceWith(sourceElement);
  }
}

/**
 * Fetch cart-drawer section HTML as fallback when cart/update response has no sections
 */
async function fetchCartDrawerSectionHtml() {
  const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.text();
}

/**
 * Update cart drawer DOM after discount changes; throws on fetch or DOM replacement failure
 */
async function refreshCartDrawer(sectionHtml) {
  const html = sectionHtml || await fetchCartDrawerSectionHtml();
  replaceCartDrawerFromHtml(html);

  if (window.discountApplyInstance) {
    window.discountApplyInstance.reinitialize();
  } else {
    window.discountApplyInstance = new DiscountApply();
  }

  setTimeout(cleanupDuplicateRemoveDiscountPopups, 100);
  setTimeout(() => {
    (async () => {
      if (!window.Glood?.upsell) return;
      await Glood.upsell.refresh({ refetchCart: true });
      await Glood.upsell.miniCart?.hydrate?.({
        freeGiftSurface: 'cart'
      });
    })();
  });
}

class DiscountApply {
  constructor() {
    this.form = document.getElementById('discount-apply-form');
    this.input = document.getElementById('discount-code-input');
    this.button = document.getElementById('discount-apply-button');
    this.buttonText = document.getElementById('discount-button-text');
    this.loading = document.getElementById('discount-loading');
    this.errorElement = document.getElementById('discount-error');
    this.successElement = document.getElementById('discount-success');
    this.tagsContainer = document.getElementById('discount-tags');

    // Abort controller for managing requests
    this.activeFetch = null;
  }

  /**
   * Refresh DOM references after cart drawer re-renders
   */
  reinitialize() {
    this.form = document.getElementById('discount-apply-form');
    this.input = document.getElementById('discount-code-input');
    this.button = document.getElementById('discount-apply-button');
    this.buttonText = document.getElementById('discount-button-text');
    this.loading = document.getElementById('discount-loading');
    this.errorElement = document.getElementById('discount-error');
    this.successElement = document.getElementById('discount-success');
    this.tagsContainer = document.getElementById('discount-tags');
  }

  /**
   * Create abort controller for request management
   */
  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }
    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  /**
   * Get currently applied discount codes from DOM
   */
  getExistingDiscounts() {
    const discountCodes = [];
    const discountTags = document.querySelectorAll('.discount-apply__tag[data-discount-code]');
    
    discountTags.forEach(tag => {
      const code = tag.getAttribute('data-discount-code');
      if (code) {
        discountCodes.push(code);
      }
    });

    return discountCodes;
  }

  /**
   * Apply discount code using fetch API
   */
  async handleSubmit(event) {
    // Resolve fresh DOM references from the event target to avoid stale nodes after re-renders
    const container = (event && event.target && event.target.closest && event.target.closest('#discount-apply-form'))
      || document.getElementById('discount-apply-form');

    const inputEl = (container && container.querySelector('#discount-code-input'))
      || document.getElementById('discount-code-input');
    const buttonEl = (container && container.querySelector('#discount-apply-button'))
      || document.getElementById('discount-apply-button');
    const loadingEl = (container && container.querySelector('#discount-loading'))
      || document.getElementById('discount-loading');
    const buttonTextEl = (container && container.querySelector('#discount-button-text'))
      || document.getElementById('discount-button-text');
    const errorEl = (container && container.querySelector('#discount-error'))
      || document.getElementById('discount-error');
    const successEl = (container && container.querySelector('#discount-success'))
      || document.getElementById('discount-success');

    // Update instance refs to current DOM so UI helpers operate on live nodes
    if (inputEl) this.input = inputEl;
    if (buttonEl) this.button = buttonEl;
    if (loadingEl) this.loading = loadingEl;
    if (buttonTextEl) this.buttonText = buttonTextEl;
    if (errorEl) this.errorElement = errorEl;
    if (successEl) this.successElement = successEl;

    if (!inputEl) {
      return;
    }

    const discountCode = (inputEl.value || '').trim().toUpperCase();
    
    if (!discountCode) {
      return;
    }

    const existingDiscounts = this.getExistingDiscounts();
    
    if (existingDiscounts.includes(discountCode)) {
      this.showError('alreadyApplied');
      this.input.value = '';
      return;
    }

    this.showLoading(true);
    this.hideMessages();

    const abortController = this.createAbortController();

    try {
      // Prepare the discount codes array
      const allDiscounts = [...existingDiscounts, discountCode];
      
      // Prepare request body
      const body = JSON.stringify({
        discount: allDiscounts.join(','),
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      });

      // Make fetch request to update cart with discount
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body,
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Require the submitted code in discount_codes with applicable: true
      let isValid = false;
      let errorMessageKey = 'invalid';

      if (data.errors) {
        errorMessageKey = 'unableToApply';
      } else if (Array.isArray(data.discount_codes)) {
        const targetDiscount = data.discount_codes.find(
          (discount) => discount.code?.toUpperCase() === discountCode
        );

        if (targetDiscount?.applicable) {
          isValid = true;
        }
      }

      if (!isValid) {
        this.showError(errorMessageKey);
        this.input.value = '';
        return;
      }

      try {
        await refreshCartDrawer(data.sections?.['cart-drawer']);
        this.input.value = '';
        this.showSuccess('applySuccess');
      } catch (refreshError) {
        this.reinitialize();
        this.showError('refreshFailed');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        this.reinitialize();
        this.showError('applyFailed');
      }
    } finally {
      this.showLoading(false);
      this.activeFetch = null;
    }
  }

  showLoading(show) {
    this.button.disabled = show;
    this.loading.classList.toggle('show', show);
    this.buttonText.style.display = show ? 'none' : 'inline';
  }

  showError(messageKey) {
    this.errorElement.textContent = DISCOUNT_APPLY_MESSAGES[messageKey] || '';
    this.errorElement.classList.add('show');
    this.successElement.classList.remove('show');
  }

  showSuccess(messageKey) {
    this.successElement.textContent = DISCOUNT_APPLY_MESSAGES[messageKey] || '';
    this.successElement.classList.add('show');
    this.errorElement.classList.remove('show');
  }

  hideMessages() {
    this.errorElement.classList.remove('show');
    this.successElement.classList.remove('show');
  }
}

// Global variable to store discount code to be removed
let discountCodeToRemove = null;

/**
 * Show remove discount confirmation popup
 */
function removeDiscount(discountCode) {
  // Store the discount code to be removed
  discountCodeToRemove = discountCode;
  
  // Update the popup with the discount code
  const discountCodeSpan = document.getElementById('discount-code-to-remove');
  if (discountCodeSpan) {
    discountCodeSpan.textContent = discountCode;
  }
  
  // Load UgCommonPopup script if not already loaded
  if (!customElements.get('ug-common-popup')) {
    const script = document.createElement('script');
    script.src = document.querySelector('[src*="ug-popup.js"]')?.src || '/assets/ug-popup.js';
    document.head.appendChild(script);
    
    // Wait for custom element to be defined before proceeding
    customElements.whenDefined('ug-common-popup').then(() => {
      openRemoveDiscountPopup(discountCode);
    });
    return;
  }
  
  openRemoveDiscountPopup(discountCode);
}

/**
 * Clean up duplicate remove discount popups
 */
function cleanupDuplicateRemoveDiscountPopups() {
  const allPopups = document.querySelectorAll('#remove-discount-popup');
  
  // If there's only one or no popup, return the first one (or null)
  if (allPopups.length <= 1) {
    return allPopups[0] || null;
  }
  
  // Keep the one that's already in body, or the first one
  let keepPopup = Array.from(allPopups).find(popup => popup.parentNode === document.body) || allPopups[0];
  
  // Remove all others
  allPopups.forEach(popup => {
    if (popup !== keepPopup) {
      // Ensure popup is closed before removing
      if (popup.isVisible) {
        popup.close();
      }
      popup.remove();
    }
  });
  
  return keepPopup;
}

/**
 * Helper function to open the remove discount popup
 */
function openRemoveDiscountPopup(discountCode) {
  // Clean up any duplicate popups first
  let popup = cleanupDuplicateRemoveDiscountPopups();
  
  // If no popup found, try to get it directly
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  if (!popup) {
    console.error('Remove discount popup not found');
    return;
  }
  
  // Ensure popup is properly attached to body
  if (popup.parentNode !== document.body) {
    document.body.appendChild(popup);
  }
  
  // Reset popup state in case it's stuck from previous interaction
  popup.removeAttribute('visible');
  popup.isVisible = false;
  
  // Small delay to ensure proper reset before opening
  setTimeout(() => {
    popup.open();
  }, 10);
}

/**
 * Cancel remove discount action
 */
function cancelRemoveDiscount() {
  discountCodeToRemove = null;
  
  // Try to find the active popup
  let popup = document.body.querySelector('#remove-discount-popup');
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  if (popup) {
    popup.close();
  }
}

/**
 * Confirm remove discount action
 */
async function confirmRemoveDiscount() {
  if (!discountCodeToRemove) return;
  
  // Get popup reference before any DOM manipulation
  let popup = document.body.querySelector('#remove-discount-popup');
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  // Close the popup immediately for better user experience
  if (popup) {
    popup.close();
  }
  
  try {
    // Get current discount codes and remove the specified one
    const discountTags = document.querySelectorAll('.discount-apply__tag[data-discount-code]');
    const existingDiscounts = [];
    
    discountTags.forEach(tag => {
      const code = tag.getAttribute('data-discount-code');
      if (code && code !== discountCodeToRemove) {
        existingDiscounts.push(code);
      }
    });

    // Prepare request body
    const body = JSON.stringify({
      discount: existingDiscounts.join(','),
      sections: ['cart-drawer'],
      sections_url: window.location.pathname
    });

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    await refreshCartDrawer(data.sections?.['cart-drawer']);

    } catch (error) {
      console.error('Failed to remove discount code:', error);
      // Note: Popup is already closed at the beginning of the function
    } finally {
      // Reset the stored discount code
      discountCodeToRemove = null;
    }
}

// Initialize discount apply component
document.addEventListener('DOMContentLoaded', function() {
  if (!window.discountApplyInstance) {
    try {
      window.discountApplyInstance = new DiscountApply();
    } catch (error) {
      // Handle error silently
    }
  }

  setupDiscountApplyDelegation();
});

/**
 * Single delegated listener set — survives DOM replacements without duplicate direct bindings
 */
function setupDiscountApplyDelegation() {
  if (window.__discountApplyDelegationAdded) return;
  window.__discountApplyDelegationAdded = true;

  function getDiscountApplyInstance() {
    if (!window.discountApplyInstance) {
      window.discountApplyInstance = new DiscountApply();
    }
    return window.discountApplyInstance;
  }

  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target?.closest?.('#discount-apply-button');
      if (!btn) return;
      e.preventDefault();
      try {
        getDiscountApplyInstance().handleSubmit(e);
      } catch (err) {
        // Handle error silently
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (e) {
      const input = e.target?.closest?.('#discount-code-input');
      if (!input || e.key !== 'Enter') return;
      e.preventDefault();
      try {
        getDiscountApplyInstance().handleSubmit(e);
      } catch (err) {
        // Handle error silently
      }
    },
    true
  );
}



// Make functions globally available
window.removeDiscount = removeDiscount;
window.cleanupDuplicateRemoveDiscountPopups = cleanupDuplicateRemoveDiscountPopups;
window.openRemoveDiscountPopup = openRemoveDiscountPopup;
window.cancelRemoveDiscount = cancelRemoveDiscount;
window.confirmRemoveDiscount = confirmRemoveDiscount;
